import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  capacityPausedText,
  countedNote,
  formatTime,
  isTrialPlan,
  notEnoughText,
  outcomeNote,
  userMessageText,
} from "./studioText";
import { AiFirstUseNotice, AiMistakesNote } from "../AiNotice/AiNotice";

/* The conversation with the AI. Purely presentational: StudioWorkspace owns
   the messages, the live turn and the send/stop actions.

   Bubbles follow docs/lp11-theme-spec.md: the client's own messages in the
   purple action gradient on the right, the AI's in frosted navy glass on the
   left with a cyan chip. Links in AI text open in a new tab, so the Studio is
   never navigated away from by something the AI wrote.

   With a plan (`plan`, a PlanView), the composer says what Send costs
   (#studio-send-cost), and Send is aria-disabled, never disabled, when the
   plan cannot take the message, with a notice saying why and what to do.
   While a turn runs the box is read-only and the one button becomes Stop, so
   keyboard focus never drops to the page. */

export const SEND_COST_ID = "studio-send-cost";
const BLOCKED_ID = "studio-send-blocked";
// Why Continue is aria-disabled, when that is not already the Send notice.
const CONTINUE_BLOCKED_ID = "studio-continue-blocked";
const FIX_HINT_ID = "studio-fix-hint";

const markdownLinks = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

function AiBubble({ t, children, label }) {
  return (
    <div className="studio-msg studio-msg-ai">
      <div className="studio-msg__head">
        <span className="studio-msg__icon" aria-hidden="true">✦</span>
        <span className="studio-msg__label">{label || t("studio.chat.aiName")}</span>
      </div>
      {children}
    </div>
  );
}

// The message ended at its work limit with a version saved: Continue picks
// it up (the server checks the same thing).
export const canContinueFrom = (messages) => {
  const last = messages[messages.length - 1];
  return Boolean(last && last.role === "assistant" && last.outcome === "limit" && last.version != null);
};

/**
 * Why the plan cannot take a message right now, or null. `uses` is what the
 * message would cost (1, or 2 on Advanced).
 */
export function composerBlock(t, plan, model, uses, locale) {
  if (!plan) return null;
  const trial = isTrialPlan(plan);
  if (plan.capacity?.paused) {
    // On the trial nothing resets: the pause lasts until the trial ends.
    return { kind: "paused", text: capacityPausedText(t, plan, plan.resetsAt, locale), seePlans: trial };
  }
  if (!plan.canUseAi) {
    const code = {
      none: "plan_required",
      trial_ended: "trial_ended",
      lapsed: "plan_lapsed",
      pending: "plan_pending",
      suspended: "plan_suspended",
    }[plan.status];
    if (code) return { kind: "plan", text: t(`studio.errors.${code}`), seePlans: code !== "plan_pending" && code !== "plan_suspended" };
    return { kind: "plan", text: t("studio.chat.compNoAi") };
  }
  if (model && model.allowed === false) return { kind: "model", text: t("studio.errors.model_not_in_plan"), switchStandard: true };
  const day = plan.trial;
  if (day && Number(day.todayUsed) >= Number(day.dailyLimit)) {
    const retry = day.retryAt ? ` ${t("studio.chat.trialRetry", { time: formatTime(day.retryAt, locale) })}` : "";
    return { kind: "daily", text: `${t("studio.errors.trial_daily_limit")}${retry}`, seePlans: true };
  }
  const left = Number(plan.messages?.left) || 0;
  if (left < uses) {
    return {
      kind: "messages",
      // The trial's resetsAt is the day it ends, not a reset.
      text: notEnoughText(t, plan, plan.resetsAt, locale),
      switchStandard: uses > 1 && left >= 1,
      upgrade: plan.tier === "starter",
      seePlans: true,
    };
  }
  return null;
}

function StudioChat({
  messages,
  live,
  busy,
  input,
  setInput,
  onSend,
  onStop,
  notice,
  readOnly,
  plan = null,
  model = null,
  models = [],
  locale,
  onContinue,
  onFix,
  onSwitchStandard,
  fixHighlight = false,
}) {
  const { t } = useTranslation();
  const endRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, live]);

  // The textarea grows with what is typed, up to the CSS max-height.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    box.style.height = "auto";
    box.style.height = `${Math.min(box.scrollHeight, 160)}px`;
  }, [input]);

  const uses = model?.uses ?? 1;
  const block = composerBlock(t, plan, model, uses, locale);
  const left = Number(plan?.messages?.left) || 0;

  const sendBlocked = Boolean(block) || readOnly || !input.trim();
  const send = () => {
    if (!busy && !sendBlocked) onSend();
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      send();
    }
  };

  // Continue costs what the stopped message's model costs.
  const last = messages[messages.length - 1];
  const showContinue = Boolean(onContinue) && !busy && !live && canContinueFrom(messages);
  const continueUses = models.find((m) => m.id === last?.model)?.uses ?? 1;
  const continueBlock = composerBlock(t, plan, null, continueUses, locale);
  // Continue points at the Send notice when that already says why; otherwise
  // its own reason shows under it (Send can be fine while Continue, on the
  // stopped turn's model, costs more than is left).
  const continueBlockedId = continueBlock
    ? block && !busy && block.text === continueBlock.text
      ? BLOCKED_ID
      : CONTINUE_BLOCKED_ID
    : undefined;

  // "Fix what broke" uses no messages, but it is still an AI turn: refused
  // while AI is off or paused (A10).
  const fixes = plan?.fixes;
  const showFix = Boolean(onFix) && fixes?.included > 0;
  const fixLeft = Math.max(0, Number(fixes?.left) || 0);
  const fixOff = busy || readOnly || fixLeft === 0 || (block && block.kind !== "messages" && block.kind !== "model");

  const describedBy = [plan ? SEND_COST_ID : null, block ? BLOCKED_ID : null].filter(Boolean).join(" ") || undefined;

  const trialPlan = isTrialPlan(plan);
  const note = (m) => {
    const parts = [outcomeNote(t, m.outcome, m.errorCode, m, { trial: trialPlan }), countedNote(t, m)].filter(Boolean);
    return parts.length ? <div className="studio-msg__note">{parts.join(" ")}</div> : null;
  };

  return (
    <>
      <div className="studio-messages" aria-live="polite">
        {messages.length === 0 && !live && (
          <AiBubble t={t}>
            <p>{t("studio.chat.welcome")}</p>
          </AiBubble>
        )}

        {messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} className="studio-msg studio-msg-user">{userMessageText(t, m)}</div>
          ) : (
            <AiBubble key={m.id} t={t}>
              {m.text ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownLinks}>{m.text}</ReactMarkdown> : null}
              {note(m)}
              {m.toolSummary?.length > 0 && (
                <details className="studio-steps">
                  <summary>{t("studio.chat.steps", { count: m.toolSummary.length })}</summary>
                  <ul>{m.toolSummary.map((line, i) => <li key={i}>{line}</li>)}</ul>
                </details>
              )}
              {m.version && <div className="studio-steps">{t("studio.chat.savedAs", { n: m.version })}</div>}
            </AiBubble>
          )
        )}

        {showContinue && (
          <div className="studio-continue">
            <button
              type="button"
              className="studio-btn studio-btn-primary studio-btn-small"
              aria-disabled={continueBlock ? "true" : undefined}
              aria-describedby={continueBlockedId}
              onClick={() => { if (!continueBlock) onContinue(); }}
            >
              {t("studio.chat.continue", { count: continueUses })}
            </button>
            {continueBlockedId === CONTINUE_BLOCKED_ID && (
              <div className="studio-notice studio-chat-notice" id={CONTINUE_BLOCKED_ID}>
                <p>{continueBlock.text}</p>
              </div>
            )}
          </div>
        )}

        {live && (
          <AiBubble t={t}>
            {live.text ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownLinks}>{live.text}</ReactMarkdown>
            ) : (
              <span className="studio-thinking" aria-label={t("studio.chat.working")}><span /><span /><span /></span>
            )}
            {live.tools.length > 0 && (
              <ul className="studio-live-tools">{live.tools.slice(-6).map((line, i) => <li key={i}>{line}</li>)}</ul>
            )}
          </AiBubble>
        )}
        <div ref={endRef} />
      </div>

      {notice && <div className="studio-notice studio-chat-notice">{notice}</div>}

      {block && !busy && (
        <div className="studio-notice studio-chat-notice" role="alert" id={BLOCKED_ID}>
          <p>{block.text}</p>
          {(block.switchStandard || block.upgrade || block.seePlans) && (
            <div className="studio-notice__actions">
              {block.switchStandard && onSwitchStandard && (
                <button type="button" className="studio-btn studio-btn-dark studio-btn-small" onClick={onSwitchStandard}>
                  {t("studio.chat.switchStandard")}
                </button>
              )}
              {block.upgrade && <Link to="/accountsettings?tab=billing">{t("studio.chat.upgrade")}</Link>}
              {block.seePlans && <Link to="/studio/plans">{t("studio.chat.seePlans")}</Link>}
            </div>
          )}
        </div>
      )}

      <div className="studio-ai-notices">
        <AiFirstUseNotice />
        <AiMistakesNote />
      </div>

      <div className="studio-composer">
        <textarea
          ref={boxRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("studio.chat.placeholder")}
          aria-label={t("studio.chat.placeholder")}
          maxLength={8000}
          readOnly={busy || readOnly}
        />
        <div className="studio-composer__row">
          <span className="studio-composer__hint">
            {busy ? t("studio.chat.workingHint") : t("studio.chat.sendHint")}
            {plan && (
              <>
                {" · "}
                <span id={SEND_COST_ID}>{t("studio.chat.cost", { count: uses, left })}</span>
              </>
            )}
          </span>
          {showFix && (
            <>
              <span id={FIX_HINT_ID} className="sr-only">{t("studio.chat.fixHint")}</span>
              <button
                type="button"
                className={`studio-btn studio-btn-small ${fixHighlight && !fixOff ? "studio-btn-primary" : "studio-btn-dark"}`}
                aria-disabled={fixOff ? "true" : undefined}
                aria-describedby={FIX_HINT_ID}
                onClick={() => { if (!fixOff) onFix(); }}
              >
                {t("studio.chat.fix", { count: fixLeft })}
              </button>
            </>
          )}
          {/* One button for Send and Stop, so focus stays on it while a turn runs. */}
          <button
            type="button"
            className={`studio-btn studio-btn-small ${busy ? "studio-btn-dark" : "studio-btn-primary"}`}
            onClick={busy ? onStop : send}
            aria-disabled={!busy && sendBlocked ? "true" : undefined}
            aria-describedby={busy ? undefined : describedBy}
          >
            {busy ? t("studio.chat.stop") : t("studio.chat.send")}
          </button>
        </div>
      </div>
    </>
  );
}

export default StudioChat;
